/**
 * App-specific Electron-only channel stub TABLE — INJECTED into the
 * framework-free SDK (`@volley/crucible-client`) from the crucible-web
 * SPA boot (crucible-client-sdk-tdd.md §3.5 / §5.2 step 5).
 *
 * This table used to live inside `@volley/crucible-client`
 * (`stub-channels.ts`), but that stranded app-specific channel policy
 * inside the SDK core. The SDK core is now stub-AGNOSTIC: a
 * `BrowserCrucibleClient` constructed with no `stubChannels` option has
 * NO stubs and routes every channel over the wire. `cookie-spa-boot.ts`
 * injects THIS table via the `stubChannels` constructor option so the
 * cookie-mode SPA keeps the exact same Electron-only stub behaviour.
 *
 * When `BrowserCrucibleClient.invoke` is called with a channel that
 * has no JSON-RPC counterpart (auto-updater, local-network VWR
 * pairing, local-FS editor open, system-tool probes, local
 * credential store), the client consults the injected `stubChannels`
 * table and returns a typed sentinel synchronously instead of
 * round-tripping to the cap host. The return value's TS shape MUST
 * match the existing `InvokeChannelMap[K][1]` exactly so renderer call
 * sites continue to typecheck (plan-v2 gate 13).
 *
 * BG-5.3 expansion (codex S5 on BG-5.2): the original 10-stub
 * inventory left ~20 Electron-only channels uncovered. The lifted
 * `<App />` calls many of these at MOUNT time (ThemeProvider →
 * `config:get`; useFeatureFlags → `app:get-feature-flags`;
 * UpdateBanner → `app:get-pending-update`; WhatsNewCard →
 * `app:get-whats-new`; etc.). Without a stub, those calls would
 * round-trip JSON-RPC to a cap host that has no handler, reject
 * with method-not-found, and silently degrade the UX (feature
 * flags all OFF, no version pill, no maintainer affordances).
 *
 * Cross-reference: `docs/browser-gui/channel-disposition.md`. Every
 * channel listed as `local-Electron-only` or `rewritten` there
 * should either appear here OR be explicitly cleared as
 * "intentionally rejects in browser" (e.g. local-FS writes that
 * have no sensible no-op behaviour).
 *
 * Naming for stub-emitted state: where the channel's return type
 * has a discriminated-union slot (e.g. `VwrSsoStatus.reason`), the
 * stub fills it with the literal `'browser-stub'` so renderer code
 * can detect the path without relying on transport sniffing.
 */

import type { ElectronOnlyStubs } from '@volley/crucible-client';
import type {
  ActivityPrefs,
  CrucibleConfig,
  DashboardArchiveSetInput,
  DashboardFavoriteSetInput,
  DashboardPreferences,
  FeatureFlagsSnapshot,
  FurnaceStatus,
  InvokeChannelMap,
  SystemToolName,
  TemplateUpdateCheckResult,
  TokenStatus,
  TokenVerifyResult,
} from '@volley/crucible-ipc-types';
import { DEFAULT_FEATURE_FLAGS } from '@volley/crucible-ipc-types';
import {
  buildResolvedFoundrySettings,
  cloudFoundrySettingsCapabilities,
} from '@volley/crucible/runtime/resolved-settings.js';

/**
 * In-memory session storage for setter / getter stub pairs (codex
 * round-1 T3). Mutation stubs (config:set, app:set-analytics-opt-out,
 * etc.) write here; the matching getter stubs read here on
 * subsequent calls. Gives the browser SPA in-tab-session persistence
 * so Settings save buttons appear to work — values survive route
 * changes within the SPA but reset on a hard reload (which is
 * documented behaviour pending a cloud persistence endpoint in
 * BG-5.4+).
 *
 * NOT exported: the storage is a stub implementation detail, not a
 * public API. Callers reach the stubs through the `ELECTRON_ONLY_STUBS`
 * table (injected into `BrowserCrucibleClient` as `stubChannels`).
 */
const sessionState: {
  config?: CrucibleConfig;
  analyticsOptOut?: boolean;
  falKeyDeclined?: boolean;
  onboardingComplete?: boolean;
  dashboardPreferences?: DashboardPreferences;
} = {};

const liteStubWarnings = new Set<string>();

/** Test-only: reset the in-memory session state so each test starts clean. */
export function __resetStubSessionStateForTests(): void {
  delete sessionState.config;
  delete sessionState.analyticsOptOut;
  delete sessionState.falKeyDeclined;
  delete sessionState.onboardingComplete;
  delete sessionState.dashboardPreferences;
  liteStubWarnings.clear();
}

const DEFAULT_CONFIG: CrucibleConfig = {
  gamesDir: 'browser-stub',
  githubOrg: 'Volley-Inc',
  // Aligned with the real resilience default (fix/default-model-opus48):
  // Opus 4.8, not Fable 5.
  claudeModel: 'claude-opus-4-8',
  showTestGames: false,
  devPorts: { display: 5173, controller: 5174, server: 8080 },
  logLevel: 'info',
  theme: 'dark',
  telemetryBuildGame: true,
};

const SYSTEM_TOOL_NAMES: readonly SystemToolName[] = ['git', 'node', 'corepack', 'pnpm'];

/**
 * Mirrors the Electron `dashboardPreferenceKey` EXACTLY
 * (crucible-gui/src/main/dashboard-preferences.ts:38-44, incl. its
 * `cleanOptionalString` trim/empty semantics): community entries key by
 * the cleaned LOWERCASED `repoFullName` when present — same repo with a
 * different game slug is the SAME preference row (Codex r2 MAJOR on
 * #1531: scope+gameId-only keying left stale community duplicates) —
 * everything else keys by `scope:gameId`.
 */
function dashboardPreferenceStubKey(identity: {
  scope: 'local' | 'cloud' | 'community';
  gameId: string;
  repoFullName?: string;
}): string {
  if (identity.scope === 'community' && typeof identity.repoFullName === 'string') {
    const repoFullName = identity.repoFullName.trim();
    if (repoFullName.length > 0) return `community:${repoFullName.toLowerCase()}`;
  }
  return `${identity.scope}:${identity.gameId}`;
}

/**
 * Mirrors the Electron `cleanMetadata` (dashboard-preferences.ts:294-306,
 * Codex r3 MINOR on #1531): trim each optional string field, OMIT it
 * when empty/absent — so stub-persisted entries round-trip the same
 * cleaned metadata Electron writes.
 */
function cleanStubMetadata(input: {
  displayName?: string;
  repoFullName?: string;
  repoUrl?: string;
  heroImage?: string;
  description?: string;
}): {
  displayName?: string;
  repoFullName?: string;
  repoUrl?: string;
  heroImage?: string;
  description?: string;
} {
  const clean = (v: unknown): string | undefined => {
    if (typeof v !== 'string') return undefined;
    const trimmed = v.trim();
    return trimmed.length > 0 ? trimmed : undefined;
  };
  const out: Record<string, string> = {};
  const displayName = clean(input.displayName);
  const repoFullName = clean(input.repoFullName);
  const repoUrl = clean(input.repoUrl);
  const heroImage = clean(input.heroImage);
  const description = clean(input.description);
  if (displayName) out.displayName = displayName;
  if (repoFullName) out.repoFullName = repoFullName;
  if (repoUrl) out.repoUrl = repoUrl;
  if (heroImage) out.heroImage = heroImage;
  if (description) out.description = description;
  return out;
}

function warnLiteStubOnce(
  channel: 'game:list:lite' | 'game:list:local-lite' | 'game:list:cloud-lite' | 'game:status:lite',
): void {
  if (liteStubWarnings.has(channel)) return;
  liteStubWarnings.add(channel);
  if (typeof console.debug === 'function') {
    console.debug(`[browser-stub] ${channel} is unsupported in this browser runtime`);
  }
}

/**
 * One stub function per Electron-only channel. Indexed by channel
 * name; `BrowserCrucibleClient.invoke` looks the channel up here
 * (via the injected `stubChannels` option) before falling through to
 * JSON-RPC.
 *
 * The `ElectronOnlyStubs` type bound (from `@volley/crucible-client`)
 * says: each entry's return value matches the corresponding channel's
 * `[1]` slot in `InvokeChannelMap`. The `Partial` is intentional —
 * only Electron-only channels appear here; everything else routes
 * over the wire.
 */
export const ELECTRON_ONLY_STUBS: ElectronOnlyStubs = {
  // Editor launch — local FS. No-op in browser; the renderer's
  // open-in-editor button stays hidden via `capabilities.hasLocalFilesystem`.
  'game:open-in-editor': async () => undefined,

  // "+ New Game" create — FALLBACK ONLY (PKCE / browser-dev builds).
  //
  // The client-parity gate is now CLOSED (#1314). In cookie-mode,
  // `game:create` is in `COOKIE_MODE_RUNTIME_SERVED_CHANNELS`, so
  // `BrowserCrucibleClient.invoke('game:create', …)` takes its STREAMING
  // bridge: it drives the runtime's `game.create@1` over
  // `invokeStreaming`, consumes the `game.create.chunk@1` stream, fans
  // each chunk out to the `game:create:progress` push subscribers the
  // SPA already registers, and resolves the terminal `CreateGameResult`.
  // `initialPrompt` is now persisted cloud-side too. So this stub is NOT
  // reached in the cookie-mode runtime build.
  //
  // It REMAINS here as the fallback for builds with NO runtime behind
  // them (PKCE / browser-dev — `game:create` NOT in
  // `runtimeServedChannels`): the channel still has no JSON-RPC handler
  // to route to, so the stub REJECTS (rather than fabricating a fake
  // `CreateGameResult`) and the "+ New Game" flow surfaces an honest
  // "not available" error instead of a phantom success. (`invoke`'s
  // streaming-bridge branch is itself gated on `runtimeServedChannels`
  // membership, so it never fires in these builds.)
  //
  // The local-Electron create path is UNAFFECTED: it goes through the
  // main-process IPC handler (`crucible-gui/src/main/ipc-surfaces/game-create.ts`)
  // via `ElectronCrucibleClient`, never `BrowserCrucibleClient` / this
  // stub table.
  'game:create': async () => {
    throw new Error(
      'Creating a game from the browser is not available in this build — use the desktop Foundry app, or the cloud-VM SPA.',
    );
  },
  'game:list:lite': async (): Promise<InvokeChannelMap['game:list:lite'][1]> => {
    warnLiteStubOnce('game:list:lite');
    return [];
  },
  'game:list:local-lite': async (): Promise<InvokeChannelMap['game:list:local-lite'][1]> => {
    warnLiteStubOnce('game:list:local-lite');
    return [];
  },
  'game:list:cloud-lite': async (): Promise<InvokeChannelMap['game:list:cloud-lite'][1]> => {
    warnLiteStubOnce('game:list:cloud-lite');
    return [];
  },
  'game:status:lite': async (project): Promise<InvokeChannelMap['game:status:lite'][1]> => {
    warnLiteStubOnce('game:status:lite');
    return {
      gameId: project.gameId,
      localGit: { exists: false },
      bifrost: { exists: false },
      registry: { exists: false },
      statusBadge: 'draft',
    };
  },

  // VWR (Volley Wireless Remote) device pairing — needs local network
  // access. Browser SPA hides VWR affordances via
  // `capabilities.hasLocalNetwork`, but a defensive stub returns the
  // 'not logged in + reason' shape so any straggler call gets a
  // typed result.
  'vwr:sso-status': async () => ({
    loggedIn: false,
    profile: 'browser-stub',
    reason: 'VWR is not available in the browser SPA',
  }),
  'vwr:sso-login': async () => ({
    success: false,
    exitCode: 1,
    error: 'VWR is not available in the browser SPA',
  }),
  'vwr:setup-device': async () => ({
    success: false,
    exitCode: 1,
    error: 'VWR is not available in the browser SPA',
  }),

  // ProtoHub native simulator (crucible#2325). `protohub:*` open a
  // NATIVE Electron `BrowserView` via an ipcMain handler, so they are
  // host-only and have no JSON-RPC counterpart on the pod. Without
  // these stubs the preload-less cookie-mode SPA forwarded
  // `protohub:open` → the pod as `protohub.open@1` → "Method not
  // found: protohub.open@1" (and `set-bounds`/`reload`/`close` would
  // spam `method_not_found`, cf. #1797). The Workspace already gates
  // its only cloud caller (the post-deploy auto-open) on the
  // `hasNativeSimulator` capability, so these are defense-in-depth:
  // an HONEST reject — never a fake `{ ok: true }`, which would hang
  // `ProtoHubSimulatorSurface` forever on "Loading ProtoHub" (its
  // `protohub:load-state` push never arrives in the SPA). Mirrors the
  // VWR `hasLocalNetwork`-gated stubs above.
  'protohub:open': async () => ({
    ok: false,
    error: 'ProtoHub is not available in the browser SPA',
  }),
  'protohub:set-bounds': async () => undefined,
  'protohub:reload': async () => undefined,
  'protohub:close': async () => undefined,

  // GitHub identity chip (crucible#2894). `github:get-user` is served
  // by an ipcMain handler that reads the LOCAL GitHub credential
  // store — host-only, no JSON-RPC counterpart on the pod. It was
  // missing from this table AND from `COOKIE_MODE_RUNTIME_SERVED_
  // CHANNELS`, so the Dashboard / NewGame `useIpcQuery('github:get-
  // user')` mounts fell through `BrowserCrucibleClient.invoke` to the
  // wire as `github.get-user@1` → "Method not found" on every SPA
  // dashboard load (the redacted `method_not_found` bursts in #2894;
  // masked in dev by `local-cloud-mock.ts`, which fakes the method).
  // `null` is the channel's typed "no user" result — the renderer
  // simply omits the avatar chip, same as a signed-out desktop.
  'github:get-user': async () => null,

  // Auto-updater — handled in browser by the version-poll banner
  // (BG-4). The Settings pane gates the auto-updater UI behind
  // `capabilities.hasAutoUpdater`, but stubs keep typecheck happy.
  'app:check-updates': async () => ({ available: false }),
  'app:install-update': async () => undefined,
  'app:get-pending-update': async () => null,

  // Local logs path — opens the Electron app's log directory in a
  // file manager. No analogue in browser; the SPA shows a download
  // link to a diagnostic bundle instead (BG-5.1).
  'app:open-logs': async () => undefined,

  // System-tool probes (git/node/pnpm/corepack) — only meaningful on
  // the local FS. Returns each tool with `resolvedPath: null` +
  // 'browser-stub' resolverError so the diagnostics surface degrades
  // cleanly.
  'diagnostics:probe-system-tools': async () =>
    Object.fromEntries(
      SYSTEM_TOOL_NAMES.map((cmd) => [
        cmd,
        {
          cmd,
          resolvedPath: null,
          resolverError: 'browser-stub: no local filesystem',
        },
      ]),
    ) as InvokeChannelMap['diagnostics:probe-system-tools'][1],

  // Open a reviewer file in the local FS — same no-FS reasoning.
  'phase-progress:open-review': async () => ({ ok: false }),

  // ---------------------------------------------------------------
  // #3005 — channels that were silently falling through the
  // `BrowserCrucibleClient.invoke` "else forward" path (no stub, no
  // runtime handler) and flooding the furnace with `method_not_found`
  // (the "[Furnace] Unknown RPC methods" monitor). Each is either a
  // desktop-host-only surface or a cloud surface whose furnace handler
  // is a tracked follow-up; stubbing here stops the forward. The
  // cloud-served state surfaces (game-state:* / phase-progress:subscribe
  // /unsubscribe) are deliberately NOT stubbed — they now have real
  // furnace handlers and must forward.
  // ---------------------------------------------------------------

  // `phase-progress:decide-blocked` is a no-op even on the desktop (the
  // orchestration never shipped — the desktop handler itself returns
  // `{ ok: false, reason: 'not-implemented' }`), so forwarding it to the
  // furnace only ever produced method_not_found. Return the same inert
  // shape locally.
  'phase-progress:decide-blocked': async () => ({ ok: false, reason: 'not-implemented' }),

  // Deploy status watcher + failure context. On the desktop these poll
  // GitHub Actions + Bifrost + probe the TV using the LOCAL GitHub PAT
  // (`main/deploy-watch.ts` / `main/deploy-failure.ts`). The cloud twin
  // (a furnace-side deploy watcher) is a tracked follow-up (#3005); until
  // then the SPA no-ops these instead of flooding the furnace. Start
  // returns no lease, stop returns undefined, and the failure-context read
  // returns the typed "unavailable" error shape the card already renders.
  'deploy:watch:start': async () => null,
  'deploy:watch:stop': async () => undefined,
  'deploy:get-failure-context': async () => ({
    status: 'error',
    errorMessage: 'Deploy failure detail is not available in the cloud SPA yet.',
  }),
  // crucible#3495 — the "already up to date on the TV" check shells out to
  // `git fetch` against the LOCAL checkout (`core/deploy-up-to-date.ts`),
  // which the cloud SPA has no access to. Return the "unknown" shape (no
  // `upToDate` field → `undefined`): the readiness classifier fail-opens
  // on unknown, so Save-to-TV stays enabled rather than being falsely
  // blocked. Same runtime-only rationale as the deploy-watch stubs above.
  'deploy:check-up-to-date': async () => ({ checkedAt: Date.now() }),

  // Tile / thumbnail art. The desktop returns a LOCAL `file://` asset URL
  // for `<gamesDir>/<gameId>/…/tiles/<gameId>.webp` — useless in a
  // browser, which needs an HTTP URL. Serving the pod's tile bytes over
  // HTTP is a follow-up (#3005); until then return "no tile" (the header
  // falls back to its placeholder, exactly as the desktop does when no
  // tile file exists).
  'game:get-tile-image': async () => ({ assetUrl: null }),

  // ---------------------------------------------------------------
  // Dashboard favourites/archive preferences (#1530) — the 2026-06-05
  // dashboard-usability sprint stores these in a local-Electron JSON
  // file (`dashboard-preferences.json` in userData) and made the
  // Dashboard's render gate include `preferencesQuery.isLoading` on
  // BOTH list paths (Dashboard.tsx:306-308). Unstubbed, the invoke
  // round-trips to a cap host with no handler and never settles —
  // the cookie-mode SPA wedges at "Loading games…" forever (the
  // web-e2e cookie-auth-dashboard-populate red). The GET stub serves
  // the SESSION-STATEFUL prefs (same codex-T3 pattern as config:set/
  // config:get above): the mutation stubs mirror the Electron
  // setters' upsert semantics (filter by dashboardPreferenceKey parity
  // — community repoFullName when present, else scope:gameId — push
  // with timestamp + cleaned metadata; dashboard-preferences.ts:138-160), so the
  // favourite/archive toggles HOLD across the Dashboard's
  // invalidate-and-refetch loop instead of silently reverting (Codex
  // r1 MAJOR on #1531). Values reset on hard reload pending a cloud
  // prefs endpoint (BG-5.4+ shape, documented in the audit doc).
  // Empty default mirrors the Electron reader's missing-file shape;
  // literal 1 = DASHBOARD_PREFERENCES_SCHEMA_VERSION (main-side
  // constant that cannot be imported here).
  'dashboard:preferences:get': async (): Promise<DashboardPreferences> =>
    sessionState.dashboardPreferences ?? {
      schemaVersion: 1,
      archived: [],
      favorites: [],
    },
  'game:archive:set': async (input: DashboardArchiveSetInput) => {
    const prefs = sessionState.dashboardPreferences ?? {
      schemaVersion: 1,
      archived: [],
      favorites: [],
    };
    const key = dashboardPreferenceStubKey(input);
    const archived = prefs.archived.filter((e) => dashboardPreferenceStubKey(e) !== key);
    if (input.archived) {
      archived.push({
        scope: input.scope,
        gameId: input.gameId,
        archivedAt: new Date().toISOString(),
        ...cleanStubMetadata(input),
      });
    }
    sessionState.dashboardPreferences = { ...prefs, archived };
  },
  'game:favorite:set': async (input: DashboardFavoriteSetInput) => {
    const prefs = sessionState.dashboardPreferences ?? {
      schemaVersion: 1,
      archived: [],
      favorites: [],
    };
    const key = dashboardPreferenceStubKey(input);
    const favorites = prefs.favorites.filter((e) => dashboardPreferenceStubKey(e) !== key);
    if (input.favorite) {
      favorites.push({
        scope: input.scope,
        gameId: input.gameId,
        favoritedAt: new Date().toISOString(),
        ...cleanStubMetadata(input),
      });
    }
    sessionState.dashboardPreferences = { ...prefs, favorites };
  },

  // ---------------------------------------------------------------
  // BG-5.3 expansion — mount-time critical local-Electron-only
  // channels. The lifted <App /> calls each of these at boot /
  // mount; without a stub they round-trip to the cap host and
  // reject with method-not-found, degrading the UX.
  // ---------------------------------------------------------------

  // Local config file (gamesDir, theme, devPorts, etc.). The browser
  // SPA can't read the user's filesystem; return a defaults snapshot
  // so ThemeProvider's queryFn (App.tsx) resolves without rejecting.
  // The shape mirrors the in-memory defaults Electron applies when
  // ~/.crucible/config.json is missing. Codex round-1 T3: serve back
  // the user's most recent config:set value within the SPA session
  // so Save buttons appear to work; values reset on hard reload
  // pending a cloud persistence endpoint in BG-5.4+.
  'config:get': async (): Promise<CrucibleConfig> => ({
    ...DEFAULT_CONFIG,
    ...(sessionState.config ?? {}),
    tier: 'prosumer',
  }),
  'config:set': async (patch): Promise<void> => {
    sessionState.config = {
      ...DEFAULT_CONFIG,
      ...(sessionState.config ?? {}),
      ...patch,
      tier: 'prosumer',
    };
  },

  // Feature-flag snapshot. The lifted useFeatureFlags hook reads
  // this on mount. Cloud/browser mode pins lazyDashboardLoad OFF so
  // the dashboard selects the owner-scoped `game:list` → `game.list@1`
  // path until `game:list:lite` has a Cloud runtime handler.
  // Browser-only flags (foundryPrototypes, cloudVmV1) stay OFF in this
  // fallback stub; real cookie-mode Cloud routes app:get-feature-flags
  // to the runtime and returns cloudVmV1 there.
  'app:get-feature-flags': async (): Promise<FeatureFlagsSnapshot> => ({
    ...DEFAULT_FEATURE_FLAGS,
    // Cloud/browser fallback pins lazyDashboardLoad OFF (selects the
    // owner-scoped game:list path) and designTab OFF; everything else
    // rides the canonical default (#2197).
    lazyDashboardLoad: false,
    designTab: false,
  }),
  'settings:get-resolved': async () =>
    buildResolvedFoundrySettings({
      placement: 'cloud',
      capabilities: cloudFoundrySettingsCapabilities(),
    }),

  // App-level state probes. Each returns a sensible "browser-side
  // defaults" value so the lifted code's render path doesn't trip
  // on null / missing fields.
  'app:get-version': async () => 'browser-spa-dev',
  'app:get-whats-new': async () => null,
  'app:dismiss-whats-new': async () => undefined,
  'app:get-furnace-status': async (): Promise<FurnaceStatus> => ({
    cloudVmV1Enabled: false,
    cohort: 'unknown',
    userId: null,
    lastBuildTarget: null,
  }),
  'app:get-vm-diagnostics': async () => null,
  // Analytics opt-out — in-session persistence (codex round-1 T3).
  'app:get-analytics-opt-out': async () => sessionState.analyticsOptOut ?? false,
  'app:set-analytics-opt-out': async (args) => {
    sessionState.analyticsOptOut = args.optOut;
  },
  'app:is-maintainer': async () => false,
  'app:get-diagnostics': async () =>
    'Diagnostics unavailable in browser SPA (Foundry running in cloud-VM mode).',
  'diagnostics:get-project': async () =>
    'Project diagnostics unavailable in browser SPA (Local projects only).',
  // Onboarding — skip by default because the browser Cognito flow
  // IS the onboarding (gate 5 in plan-v2). In-session persistence
  // for the set call so test seams work.
  'app:onboarding-complete': async () => sessionState.onboardingComplete ?? true,
  'app:set-onboarding-complete': async () => {
    sessionState.onboardingComplete = true;
  },
  'app:fal-key-declined': async () => sessionState.falKeyDeclined ?? false,
  'app:set-fal-key-declined': async (args) => {
    sessionState.falKeyDeclined = args.declined;
  },

  // Local credential store / system-tool probes.
  //
  // Browser-mode mental model (codex round-1 T1 + T2 + T4): the
  // browser SPA has NO local credentials and NO local devtools —
  // the cloud-VM runtime carries the actual Anthropic / GitHub /
  // Fal credentials, and git / node / pnpm run cloud-side. So from
  // the SPA's perspective, the answer to "are credentials configured?"
  // and "are the build tools available?" is "yes — they live in the
  // cloud runtime, not locally". Returning all-true / all-valid
  // matches that mental model and prevents the lifted callsites
  // from rendering "your tokens are missing" panels with install
  // hints that the SPA user can't act on (see
  // project_foundry_zero_local_devtools).
  //
  // The actual token validation happens server-side when the agent
  // dispatches; the browser SPA never sees the credential values.
  'tokens:get': async (): Promise<TokenStatus> => ({
    github: true,
    anthropic: true,
    fal: true,
    npmjs: true,
    'github-packages': true,
  }),
  'tokens:verify': async (): Promise<TokenVerifyResult> => ({
    github: { status: 'valid' },
    anthropic: { status: 'valid' },
    git: { available: true, version: 'cloud-runtime' },
    node: { available: true, version: 'cloud-runtime' },
    pnpm: { available: true, version: 'cloud-runtime' },
  }),
  'tokens:set': async () => undefined,
  'tokens:clear': async () => undefined,

  // Activity prefs (per-game UI dismiss state). The lifted code's
  // Workspace uses this on mount. Returning null is the "no prefs
  // saved" signal that the renderer already handles gracefully.
  'activity-prefs:load': async () => null,
  'activity-prefs:save': async () => undefined,

  // Install-state probe. Returns null = "no install activity".
  'install-state:get': async () => null,

  // Bug-report draft (local file). Returns null = "no draft".
  'bug:get-draft': async () => null,

  // Template update check. The lifted Workspace uses this to surface
  // the "template behind" banner. Return a typed error so the banner
  // falls back to the error-state copy rather than rendering stale
  // version numbers.
  'template:check-updates': async (args): Promise<TemplateUpdateCheckResult> => ({
    gameId: args.gameId,
    current: 'browser-stub',
    latest: 'browser-stub',
    hasUpdate: false,
    isBreaking: false,
    error: 'Template update check unavailable in browser SPA.',
  }),

  // ---------------------------------------------------------------
  // #1797 — Workspace interval pollers. The shared Workspace mounts
  // three pollers the moment a gameId route is active:
  //
  //   - `conflicts:get-pending`          2s  (<ConflictResolutionCard>
  //                                          via usePendingConflicts)
  //   - `build-game:interview-status`    5s  (useInterviewStatus)
  //   - `build-game:get-interview-state` 5s + a 3s safety net
  //                                          (useInterviewState +
  //                                          useInterviewStatePoll)
  //
  // All three read a local-Electron sidecar file in the game dir
  // (`.foundry/pending-conflicts.json` / `build-game-interview.json`)
  // via a main-process handler. The browser SPA has no local FS and
  // the cloud runtime has NO handler for these methods yet — so
  // unstubbed, every poll tick fell through `BrowserCrucibleClient.invoke`
  // to the wire and the pod audit-logged ~1/sec `-32601
  // method_not_found` for the whole session (187 dispatches in a 200s
  // window, observed live on staging — #1797).
  //
  // Each stub returns the channel's documented "absent" sentinel
  // (`null` = no sidecar / no interview state file), which renders
  // EXACTLY like the rejection did (the hooks expose only `data`, not
  // `error`, so consumers can't tell `undefined`-after-reject from
  // `null`) — minus the per-second wire spam. If/when these channels
  // graduate to real pod-side runtime handlers (the interview state
  // file DOES exist pod-side during a cloud build), add them to
  // `COOKIE_MODE_RUNTIME_SERVED_CHANNELS` in `cookie-spa-boot.ts` and
  // the stub is bypassed per-channel, same as `app:get-feature-flags`
  // et al. (lane A graduation pattern).
  'conflicts:get-pending': async () => null,
  'build-game:interview-status': async () => null,
  'build-game:get-interview-state': async () => null,

  // ---------------------------------------------------------------
  // #3401 — fail-closed channels invoked by browser SPA consumers
  // that have no furnace handler. Keep these calls off the wire until
  // they graduate to real handlers via COOKIE_MODE_RUNTIME_SERVED_CHANNELS
  // in cookie-spa-boot.ts (the lane-A graduation pattern above).
  // ---------------------------------------------------------------

  // `useDesignAgentStatusRecovery` in Workspace.tsx only acts on a
  // persisted `failed` marker. `null` is the documented no-marker
  // sentinel, so the hook benignly early-returns as it does when the
  // desktop breadcrumb file is absent.
  'design-agent-status:get': async () => null,

  // Level-design surface (procgen-levels) — Electron/local-only (local `design/`
  // filesystem + local Anthropic). The cloud runtime does not serve these, so they
  // get benign stubs: readers the shared DesignWorkspace mounts on open return empty
  // so the browser design tab degrades gracefully (no levels) instead of hard-failing;
  // mutations + the LLM judge/auto-revise report an honest "desktop only".
  'design:listLevels': async () => [],
  'design:readThreeCs': async () => null,
  'design:readProgression': async () => null,
  'design:readPrevisSkin': async () => null,
  'design:readPrevisImages': async () => ({}),
  'design:readBuildRender': async () => '',
  'design:captureLevelsFromBuild': async () => ({ items: [], noBuild: true }),
  'design:readLevel': async () => {
    throw new Error(
      'Reading a level is not available in the browser build — use the desktop Foundry app.',
    );
  },
  'design:writeLevel': async () => {
    throw new Error(
      'Editing levels is not available in the browser build — use the desktop Foundry app.',
    );
  },
  'design:deleteLevel': async () => {
    throw new Error(
      'Deleting levels is not available in the browser build — use the desktop Foundry app.',
    );
  },
  'design:writeThreeCs': async () => {
    throw new Error(
      'Editing the 3Cs is not available in the browser build — use the desktop Foundry app.',
    );
  },
  'design:writeProgression': async () => {
    throw new Error(
      'Editing progression is not available in the browser build — use the desktop Foundry app.',
    );
  },
  'design:writePrevisSkin': async () => {
    throw new Error(
      'Editing the previs skin is not available in the browser build — use the desktop Foundry app.',
    );
  },
  'design:applyLevelCapture': async () => {
    throw new Error(
      'Capturing build levels is not available in the browser build — use the desktop Foundry app.',
    );
  },
  'design:judgeLevel': async () => {
    throw new Error(
      'The level design review is not available in the browser build — use the desktop Foundry app.',
    );
  },
  'design:reviseLevel': async () => {
    throw new Error(
      'Auto-revise is not available in the browser build — use the desktop Foundry app.',
    );
  },

  // crucible#3041/#3516 — the deferred-Design terminal-status breadcrumb
  // (design-handoff-status.json) is a desktop-only filesystem read with no web
  // equivalent, exactly like design-agent-status above. `null` is the
  // no-marker sentinel; Workspace.tsx's durable mount-read effect only acts on
  // a truthy record (`if (!cancelled && status)`), so it benignly early-returns
  // on the web build (the live create:build-step subscribe remains the settle).
  'create:deferred-design-status:get': async () => null,

  // `enter()` in usePreviewInspector.ts treats `{ injected: 0 }` as
  // no injectable preview frames: it shows "Open the preview first,
  // then try Select again." and declines select mode without taking
  // the rejection-only console/error branch.
  'preview:inject-inspector': async () => ({ injected: 0 }),

  // ArtLibrary.refreshModels renders ModelCollection's honest empty state
  // when no browser-side models can be listed.
  'art:model:list': async () => ({ models: [] }),

  // AddModelDialog surfaces rejection inline; fabricating a record would
  // phantom-select a model that does not exist.
  'art:model:add': async () => {
    throw new Error('Adding art models is not available in the browser SPA yet.');
  },

  // IntegratedSoulInterview treats moodboard regeneration as best-effort
  // and keeps the prior image when this honest rejection is swallowed.
  'intake:regenerate-moodboard': async () => {
    throw new Error('Moodboard regeneration is not available in the browser SPA yet.');
  },
};

/**
 * Typed predicate: true when `channel` has a stub registered in the
 * app-specific table. Narrows the channel type so callers can index
 * `ELECTRON_ONLY_STUBS` without an extra null check.
 *
 * NB: `BrowserCrucibleClient` no longer uses this — it checks the
 * INJECTED `stubChannels` table directly. Retained for crucible-web /
 * test callers that want to interrogate the canonical table.
 */
export function hasElectronOnlyStub<C extends keyof InvokeChannelMap>(
  channel: C,
): channel is C & keyof ElectronOnlyStubs {
  return channel in ELECTRON_ONLY_STUBS;
}
